import { CategoryButton, ViewStoreButton } from '@/components/feature/floor-guide';
import { PaperIcon } from '@/components/icons/floor-guide';
import { NAV_KEYS } from '@/constants/keyboard';
import { BuildingMenuType } from '@/enums';
import { useAccessibilityTTS } from '@/hooks';
import type { KioskSelectedCategory } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { GroupedPoiByFloorId } from '@/types/map';
import { filterCategoriesWithMapPoi, getCategoryDisplayName, getCategoryIcon } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type Props = {
    groupedPoiByFloorId?: GroupedPoiByFloorId;
    onShowPoiList?: () => void;
    onSelectCategory?: (category: KioskSelectedCategory) => void;
    isShowCategoryList?: boolean;
    selectedCategoryCode?: string;
};

export const VerticalCategoryList = ({
    groupedPoiByFloorId,
    onShowPoiList,
    onSelectCategory,
    isShowCategoryList,
    selectedCategoryCode,
}: Props) => {
    const { t, i18n } = useTranslation();
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const isFullscreen = screenMode === 'fullscreen';
    const language = useMemo(() => i18n.language, [i18n]);
    const { speak } = useAccessibilityTTS();

    const selectedCategories = useConfigStore(
        (state) =>
            state.config.server?.buildingMenus?.[language]?.find((menu) => menu.menuType === BuildingMenuType.FLOOR)?.selectedCategories
    );

    const buildingCategories = useMemo(
        () => filterCategoriesWithMapPoi(selectedCategories, groupedPoiByFloorId),
        [selectedCategories, groupedPoiByFloorId]
    );

    const isFullHeight = Array.isArray(buildingCategories) && buildingCategories.length < 10;

    const generateIsFullWidth = useMemo(() => {
        if (isFullscreen) {
            return false;
        }
        return Array.isArray(buildingCategories) && buildingCategories.length < 10;
    }, [buildingCategories, isFullscreen]);

    const generateIsFullHeight = useMemo(() => {
        if (!isFullscreen) {
            return false;
        }
        return Array.isArray(buildingCategories) && buildingCategories.length < 10;
    }, [buildingCategories, isFullscreen]);

    return (
        <section className={clsx(styles['vertical-category-wrapper'])}>
            <div
                className={clsx(styles['vertical-category__list'], 'scrollbar-hide')}
                data-region-nav={isShowCategoryList}
                data-region-order={4}
                data-tts-scope="floor-button"
            >
                {Array.isArray(buildingCategories) &&
                    buildingCategories?.length > 0 &&
                    buildingCategories.map((item, index) => {
                        const categoryName = getCategoryDisplayName(item, language);
                        const isSelected =
                            selectedCategoryCode === item.categoryCode ||
                            item.mergeCategoryCode?.includes(selectedCategoryCode || '');

                        return (
                            <div
                                className={clsx({
                                    'shrink-0': !isFullHeight,
                                    'flex-1': isFullHeight,
                                })}
                                key={`${item.clientCode}-${index}`}
                            >
                                <CategoryButton
                                    fullWidth={generateIsFullWidth}
                                    fullHeight={generateIsFullHeight}
                                    isSelected={isSelected}
                                    icon={getCategoryIcon(item.clientCode)}
                                    onClick={() => onSelectCategory?.(item)}
                                    tabIndex={isShowCategoryList ? 4 : -1}
                                    data-interactive
                                    data-playsound="ting"
                                    onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                        if (NAV_KEYS.includes(e.key)) {
                                            speak(
                                                t('floor_guide.text_to_speech.category_btn', {
                                                    categoryName,
                                                })
                                            );
                                        }
                                    }}
                                >
                                    {categoryName}
                                </CategoryButton>
                            </div>
                        );
                    })}
            </div>
            <div className={styles['vertical-category__view-store-btn']}>
                <ViewStoreButton
                    data-region-nav={isShowCategoryList}
                    data-region-order={isShowCategoryList ? 5 : -1}
                    icon={<PaperIcon />}
                    onClick={onShowPoiList}
                    tabIndex={isShowCategoryList ? 5 : -1}
                    data-interactive
                    data-playsound="ting"
                    aria-label={t('floor_guide.button.view_store_list')}
                    data-role={t('common.button.tts.popup_button')}
                >
                    {t('floor_guide.button.view_store_list')}
                </ViewStoreButton>
            </div>
        </section>
    );
};
